import {
  detectFaceLandmarksTiny,
  FaceDetection,
  FaceLandmarks68,
  isMediaLoaded,
  loadFaceLandmarkTinyModel,
  loadTinyFaceDetectorModel,
  tf,
  tinyFaceDetector,
  TinyFaceDetectorOptions,
  TMediaElement,
} from '@vladmandic/face-api';

import { fetchModelSrc } from 'lib/assets';
import EventsClient from 'lib/clients/EventsClient';
import { captureException } from 'lib/reporting';
import { isInCypress } from 'lib/utils/dom';
import withRetries from 'lib/utils/withRetries';

class Client {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private promiseTinyFaceDetector: Promise<void>;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private promiseFaceLandmarkTiny: Promise<void>;
  private loaded = false;
  private tfOptimizationFlagsSet = false;
  private wasmPathSet = false;

  public isMediaLoaded(videoElement: HTMLVideoElement): boolean {
    return isMediaLoaded(videoElement);
  }

  public isLoaded(): boolean {
    return this.loaded;
  }

  public async initTensorflow(): Promise<void> {
    if (this.isTestMode()) return;

    try {
      /**
       * Based on this benchmark https://github.com/vladmandic/human/wiki/Backends ,
       * (This is for Human package, which is built by the same developer who made FaceAPI and he references it
       * as a successor to the FaceAPI package in the FaceAPI readme, and he also mentions it in an issue in FaceAPI)
       * The overall difference in using WASM vs WebGL is:
       * - WebGL's first startup time is much much slower, but with GPU acceleration, subsequent calls get faster
       * - WASM's startup time and subsequent calls mostly the same, but theoretically slightly slower than the
       *    fastest possible for WebGL
       * more information in this comment https://github.com/vladmandic/face-api/issues/44#issuecomment-808159440
       * for numbers in practice:
       *  - iphone 10 XR
       *    - wasm:
       *      First 10 calls (in ms):
       *      392, 154, 97, 191, 131, 163, 130, 168, 128, 154
       *      [Log] processingTime – 392 (SelfieScanner.tsx, line 356) ...
       *    - WebGL:
       *      12002, 169, 142, 129, 192, 139, 137, 130, 137, 157
       *      [Log] processingTime – 12002 (SelfieScanner.tsx, line 356)
       *      This means the UI is stuck for 12 seconds on just showing the feed, and not providing any hints
       *      (and is together with the webkit issue, it shows the tiny feed for 12s)
       *      Other attempts have had this initial processing time be 17 seconds (and outlier at 30 seconds) but on
       *      average roughly 15 seconds
       * For iPhone 10 XR at least, WASM seems almost strictly better in terms of processing times.
       * Official TFJS recommendation: https://github.com/tensorflow/tfjs/tree/master/tfjs-backend-wasm#when-should-i-use-the-wasm-backend
       * */
      if (!this.wasmPathSet) {
        // Note: If the tf version ever changes because we update the package, we also have to update
        // the wasm files we are hosting in vendor/models/tfjs-backend-wasm
        const wasmPath = fetchModelSrc('tfjs-backend-wasm') + '/';

        // The TFJS vendored into face-api doesn't have this type defined, but it's there.
        // More documentation on setWasmPaths: https://github.com/tensorflow/tfjs/tree/master/tfjs-backend-wasm#using-bundlers
        // | Note that if you call setWasmPaths with a string, it will be used to load each binary (SIMD-enabled, threading-enabled, etc.)
        // So, this would load the 3 binaries we have in vendor/models/tfjs-backend-wasm/ , and the runtime will load the
        // appropriate supported binary (https://github.com/tensorflow/tfjs/tree/master/tfjs-backend-wasm#do-you-support-simd-and-multi-threading)
        // We ALWAYS load the wasm path because FaceAPI automatically falls back to WASM if WebGL is not supported
        tf.setWasmPaths(wasmPath);
        this.wasmPathSet = true;
      }

      this.setTfOptimizationFlags();

      // Assert TFJS backends are ready.
      // The TFJS vendored into face-api doesn't have this type defined, but it's there.
      await tf.ready();

      if (!this.loaded) {
        EventsClient.logAmplitude('inquiry.tf.loaded', {
          backend: tf.getBackend(),
        });
      }
    } catch (e) {
      // Continue with loading if the above didn't hit return but capture exception so we can debug why it failed
      captureException(e, 'warning', {
        source: 'FaceApiClient.loadModels',
        wasmPathSet: this.wasmPathSet,
      });
    }
  }

  public async loadModels(): Promise<void> {
    if (this.isTestMode()) return;

    await this.initTensorflow();

    await withRetries(async () => {
      if (this.promiseTinyFaceDetector == null) {
        const tinyFaceDetectorModelSrc = fetchModelSrc('tiny_face_detector_model');

        if (tinyFaceDetectorModelSrc != null) {
          // Only attempt to load models if source is present
          this.promiseTinyFaceDetector = loadTinyFaceDetectorModel(tinyFaceDetectorModelSrc);
        }
      }

      if (this.promiseFaceLandmarkTiny == null) {
        const faceLandmarkModel = fetchModelSrc('face_landmark_68_tiny_model');

        if (faceLandmarkModel != null) {
          // Only attempt to load models if source is present
          this.promiseFaceLandmarkTiny = loadFaceLandmarkTinyModel(faceLandmarkModel);
        }
      }

      await Promise.all([this.promiseTinyFaceDetector, this.promiseFaceLandmarkTiny]);

      this.loaded = true;
    });
  }

  public async detectFaces(
    mediaElement: TMediaElement,
    { inputSize, scoreThreshold }: Partial<TinyFaceDetectorOptions>,
  ): Promise<FaceDetection[]> {
    // Default input size is 416.
    // Using 128 cuts processing from ~100ms to ~40ms but decreasing accuracy in
    // detecting small faces. We could consider only running detection on the left / right borders.
    return await tinyFaceDetector(
      mediaElement,
      new TinyFaceDetectorOptions({
        inputSize: inputSize ?? 416,
        scoreThreshold: scoreThreshold ?? 0.4,
      }),
    );
  }

  public async detectLandmarks(croppedFaceImage: HTMLCanvasElement): Promise<FaceLandmarks68> {
    return (await detectFaceLandmarksTiny(croppedFaceImage)) as FaceLandmarks68;
  }

  /**
   * For testing purposes.
   */
  public resetLoadStatus(): void {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.promiseTinyFaceDetector = undefined;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.promiseFaceLandmarkTiny = undefined;
  }

  private isTestMode(): boolean {
    return isInCypress() || import.meta.env.MODE === 'test';
  }

  private setTfOptimizationFlags(): void {
    if (this.tfOptimizationFlagsSet) return;

    try {
      /**
       * If the total number of bytes allocated on the GPU is greater than this
       * number, we will aggressively delete textures upon disposal with
       * gl.deleteMatrixTexture, rather than making them available for reuse.
       *
       * Default value -1 indicates that we will never aggressively delete textures.
       */
      tf.ENV.set('WEBGL_DELETE_TEXTURE_THRESHOLD', 0);
    } catch (err) {
      captureException(err, 'warning');
    }

    try {
      /**
       * Whether to enable canvas2d willReadFrequently for GPU backends
       */
      tf.ENV.set('CANVAS2D_WILL_READ_FREQUENTLY_FOR_GPU', true);
    } catch (err) {
      captureException(err, 'warning');
    }

    try {
      /**
       * Whether we will use the experimental conv op.
       * (@thwang ^ this is a very poor flag description...)
       * Supposedly gives massive performance improvements
       * https://github.com/tensorflow/tfjs/issues/6678#issuecomment-1196108075
       **/
      tf.ENV.set('WEBGL_EXP_CONV', true);
    } catch (err) {
      captureException(err, 'warning');
    }

    // if above fails, we probably don't want to call it repeatedly
    this.tfOptimizationFlagsSet = true;
  }
}

export const FaceApiClient = new Client();
